import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { IRRData } from '../../../models/IRRDataPoint';
import {
  formatCurrency,
  formatMetricMultiplier,
  formatMetricPercent,
  isInvalidNumber,
} from './irrFormatUtils';

interface IRRDataTableProps {
  irrData?: IRRData;
  dataLoading: boolean;
}

const summaryRowSx = { backgroundColor: '#f5f5f5', fontWeight: 'bold' };

const IRRDataTable: React.FC<IRRDataTableProps> = ({ irrData, dataLoading }) => {
  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!irrData) {
    return (
      <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
        No IRR data available for the selected month.
      </Typography>
    );
  }

  const { overall, historical } = irrData;

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Date</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Discrete Value</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Cumulative Value</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {historical
            .sort((a, b) => a.month.localeCompare(b.month))
            .map((row) => (
              <TableRow
                key={row.month}
                sx={{
                  fontStyle: row.type === 'forecast' ? 'italic' : 'normal',
                  opacity: row.type === 'forecast' ? 0.8 : 1,
                }}
              >
                <TableCell>{row.label}</TableCell>
                <TableCell
                  align="right"
                  sx={
                    isInvalidNumber(row.discrete_value)
                      ? { color: 'text.secondary', fontStyle: 'italic' }
                      : undefined
                  }
                >
                  {formatCurrency(row.discrete_value)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={
                    isInvalidNumber(row.cumulative_value)
                      ? { color: 'text.secondary', fontStyle: 'italic' }
                      : undefined
                  }
                >
                  {formatCurrency(row.cumulative_value)}
                </TableCell>
              </TableRow>
            ))}

          <TableRow sx={summaryRowSx}>
            <TableCell>
              <strong>IRR</strong>
            </TableCell>
            <TableCell align="right">
              <strong>{formatMetricPercent(overall.IRR)}</strong>
            </TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
          <TableRow sx={summaryRowSx}>
            <TableCell>
              <strong>ROE</strong>
            </TableCell>
            <TableCell align="right">
              <strong>{formatMetricPercent(overall.ROE)}</strong>
            </TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
          <TableRow sx={summaryRowSx}>
            <TableCell>
              <strong>MM</strong>
            </TableCell>
            <TableCell align="right">
              <strong>{formatMetricMultiplier(overall.MM)}</strong>
            </TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default IRRDataTable;
